import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import { useDiscoveryRequestTask } from "../contexts/DiscoveryRequestTaskContext";
import { Field, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Input } from "../design/input";
import { Textarea } from "../design/textarea";
import { DiscoveryRequest } from "../models/DiscoveryRequest";
import SlateEditor, {
  SlateEditorRef,
} from "../components/SlateTextArea/SlateTextArea";
import ReplyListItem from "../components/CommentListItem/components/ReplyListItem";
import { Comment } from "../models/Comment";
import { useComment } from "../contexts/CommentContext";
import { Descendant } from "slate";

interface Context {
  openDialog: (commentData: Comment) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

const ReplyDialogProviderContext = createContext<Context | undefined>(
  undefined,
);

export const ReplyDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createReply } = useComment();
  const [comment, setComment] = useState<Comment | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<Descendant[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<SlateEditorRef | null>(null);

  const openDialog = (commentData: Comment) => {
    setComment(commentData);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setComment(undefined);
  };

  const handleSubmit = useCallback(async () => {
    if (!comment) return;

    setIsSaving(true);
    try {
      await createReply(comment._id, {
        parentType: "Comment",
        parent: comment._id,
        content: JSON.stringify(content),
        comments: [],
      });

      setContent(null);
      editorRef.current?.resetEditor();
      setIsOpen(false);
    } catch (error) {
      console.error("Error adding reply:", error);
    } finally {
      setIsSaving(false);
    }
  }, [content, comment, createReply]);

  return (
    <ReplyDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      {comment && (
        <Dialog open={isOpen} onClose={closeDialog}>
          <DialogTitle>Create Reply</DialogTitle>
          <DialogBody>
            {comment.comments.map((reply) => (
              <ReplyListItem key={reply._id} reply={reply} />
            ))}
          </DialogBody>
          <DialogActions>
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                color: "red",
              }}
            >
              <SlateEditor
                id={comment._id}
                placeholder="Write a reply..."
                ref={editorRef}
                initialValue={content ?? undefined}
                onChange={setContent}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <Button color="zinc" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button
                  color="green"
                  onClick={handleSubmit}
                  disabled={isSaving || !comment}
                >
                  {isSaving ? "Posting..." : "Post"}
                </Button>
              </div>
            </div>
          </DialogActions>
        </Dialog>
      )}
      {children}
    </ReplyDialogProviderContext.Provider>
  );
};

export const useReplyDialog = () => {
  const ctx = useContext(ReplyDialogProviderContext);
  if (!ctx)
    throw new Error("useReplyDialog must be used inside ReplyDialogProvider");
  return ctx;
};
